import React from 'react';
import { EmblaOptionsType } from 'embla-carousel';
import { useEmblaCarousel, EmblaCarousel, UseEmblaCarousel } from 'embla-carousel/react';

type UseSliderResult = {
  embla?: EmblaCarousel;
  ref: UseEmblaCarousel[0];
  canGoPrev: boolean;
  canGoNext: boolean;
  selectedIndex: number;
  scrollSnaps: number[];
  handleScrollPrev: () => void;
  handleScrollNext: () => void;
  handleScrollTo: (index: number) => void;
};

export const useSlider = (options?: EmblaOptionsType): UseSliderResult => {
  const [ref, embla] = useEmblaCarousel({
    loop: false,
    containScroll: 'trimSnaps',
    ...options,
  });
  const [canGoPrev, setCanGoPrev] = React.useState(false);
  const [canGoNext, setCanGoNext] = React.useState(false);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [scrollSnaps, setScrollSnaps] = React.useState<number[]>([]);

  const handleScrollPrev = React.useCallback(() => embla && embla.scrollPrev(), [embla]);
  const handleScrollNext = React.useCallback(() => embla && embla.scrollNext(), [embla]);
  const handleScrollTo = React.useCallback((index) => embla && embla.scrollTo(index), [embla]);

  const onSelect = React.useCallback(() => {
    if (!embla) return;

    setSelectedIndex(embla.selectedScrollSnap());
    setCanGoPrev(embla.canScrollPrev());
    setCanGoNext(embla.canScrollNext());
  }, [embla, setSelectedIndex]);

  React.useEffect(() => {
    if (!embla) return;
    onSelect();
    setScrollSnaps(embla.scrollSnapList());
    embla.on('select', onSelect);
  }, [embla, setScrollSnaps, onSelect]);

  return {
    ref,
    embla,
    canGoPrev,
    canGoNext,
    selectedIndex,
    scrollSnaps,
    handleScrollPrev,
    handleScrollNext,
    handleScrollTo,
  };
};
